import parsePhoneNumber, { PhoneNumber } from 'libphonenumber-js';
import { uniqueId } from 'lodash';

import { OrganizationResponse, OrganizationWithUserGroupsResponse, UserResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { IdObject, UserResponseWithUserGroup, WithKey } from './types';
import { removeStorageItems } from './browserStorage';
import { SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';
import { LocalBundleEnvelopeWithChosenMainVariant } from '@cdaas/reusable-components-daas/dist/src/product-api-types/api-types';

export const typeGuards = {
  isLocalBundleEnvelopeWithChosenMainVariant: (object: unknown): object is LocalBundleEnvelopeWithChosenMainVariant => {
    const localBundleEnvelope = object as LocalBundleEnvelopeWithChosenMainVariant;
    return (
      localBundleEnvelope.bundle !== undefined &&
      localBundleEnvelope.variant_images !== undefined &&
      localBundleEnvelope.chosenMainItemVariantId !== undefined
    );
  },
  isOrganization: (object: unknown): object is OrganizationResponse => {
    const organization = object as OrganizationResponse;
    return (
      organization.call_off_number !== undefined &&
      organization.default_billing_contact !== undefined &&
      organization.name !== undefined &&
      organization.organization_number !== undefined
    );
  },
  isOrganizationWithUserGroups: (object: unknown): object is OrganizationWithUserGroupsResponse => {
    const organization = object as OrganizationWithUserGroupsResponse;
    return (
      organization.call_off_number !== undefined &&
      organization.default_billing_contact !== undefined &&
      organization.name !== undefined &&
      organization.organization_number !== undefined
    );
  },
  isSalesChannel: (object: unknown): object is SalesChannel => {
    const salesChannel = object as SalesChannel;
    return (
      salesChannel.country_id !== undefined &&
      salesChannel.id !== undefined &&
      salesChannel.locale_ids !== undefined &&
      salesChannel.name !== undefined
    );
  },
  isUser: (object: unknown): object is UserResponse => {
    const user = object as UserResponse;
    return (
      user.email !== undefined &&
      user.given_name !== undefined &&
      user.family_name !== undefined &&
      user.phone_number !== undefined &&
      user.user_roles !== undefined
    );
  },
  isUserWithUserGroup: (object: unknown): object is UserResponseWithUserGroup => {
    const user = object as UserResponseWithUserGroup;
    // this is effectively convenience function for typing,
    // as user_group is optional in UserResponseWithUserGroup
    return typeGuards.isUser(user);
  },
};

export const parsingHelpers = {
  extractPartsFromFullPhoneNumber: (phoneNumber?: string): PhoneNumber | undefined => (phoneNumber ? parsePhoneNumber(phoneNumber) : undefined),
};

export const addKey = <T>(entity: T): WithKey<T> => ({
  key: uniqueId(),
  ...entity,
});

export const getIdAndKeyString = (entity: IdObject) => `${entity.id || ''},${entity.key || ''}`;

export const clearSessionStorageOrderState = () =>
  removeStorageItems(['tItem', 'tOOB', 'tOrderForOrganization', 'tSalesChannel', 'tSelectedOrganization'], 'session');

export const compareIds = <T extends IdObject>(a?: T, b?: T): boolean => !!(a && b && ((a.id && a.id === b.id) || (a.key && a.key === b.key)));

export const compareIdAndKeyString = (string1?: string, string2?: string) => {
  if (string1 && string2) {
    const [id1, key1] = string1.split(',');
    const [id2, key2] = string2.split(',');
    return (id1 && id1 === id2) || (key1 && key1 === key2);
  } else return false;
};
